import { RegisteredComponent } from "@builder.io/sdk-react";
import {
  DLD_FIELD,
  DLT_FIELD,
  LEGACY_HREF_FIELD,
  TITLE_FIELD,
} from "../commonFields";

export const secondaryNavigationBlockConfig: Omit<
  RegisteredComponent,
  "component"
> = {
  name: "Secondary navigation",
  image:
    "https://icons.veryicon.com/png/o/miscellaneous/bim-management-platform/tree-type-1.png",
  defaultStyles: {
    display: "block",
    position: "sticky",
    top: "-1px",
    zIndex: "32",
  },
  shouldReceiveBuilderProps: {
    builderBlock: true,
    builderContext: true,
    builderComponents: true,
    builderLinkComponent: true,
  },
  inputs: [
    {
      name: "newSecondaryNavigation",
      type: "boolean",
      friendlyName: "New Secondary Navigation",
      helperText:
        "When enabled, the new secondary navigation update with Product navigation rendering will be used.",
      required: false,
      localized: false,
      defaultValue: false,
    },
    {
      name: "enableProductLink",
      type: "boolean",
      friendlyName: "Enable Product Link",
      helperText:
        "When enabled, displays as a regular link; when disabled, displays as text.",
      required: false,
      localized: false,
      defaultValue: true,
    },
    {
      name: "productLink",
      friendlyName: "Product Link",
      helperText:
        "Displays the product link when both title and href are filled; otherwise, nothing is shown.",
      type: "object",
      subFields: [
        { ...TITLE_FIELD, required: false },
        { ...LEGACY_HREF_FIELD, required: false },
        { ...DLD_FIELD, required: false },
        { ...DLT_FIELD, required: false },
      ],
      defaultValue: {
        title: {
          "@type": "@builder.io/core:LocalizedValue",
          Default: "Lorem ipsum dolor",
        },
        href: {
          "@type": "@builder.io/core:LocalizedValue",
          Default: "#",
        },
        dlt: "Secondary navigation",
      },
    },
    {
      name: "mobileMenuButton",
      type: "object",
      subFields: [
        { ...TITLE_FIELD, required: false },
        { ...DLD_FIELD, required: false },
        { ...DLT_FIELD, required: false },
      ],
      defaultValue: {
        title: {
          "@type": "@builder.io/core:LocalizedValue",
          Default: "Menu",
        },
        dlt: "Secondary navigation",
      },
    },
    {
      name: "mobileBacklink",
      friendlyName: "Mobile Backlink",
      type: "object",
      subFields: [
        {
          name: "mainMenuText",
          type: "text",
          friendlyName: "Main Menu Text",
          required: false,
          localized: true,
        },
        {
          name: "backToText",
          type: "text",
          friendlyName: "Back To Text",
          required: false,
          localized: true,
        },
        { ...DLD_FIELD, required: false },
        { ...DLT_FIELD, required: false },
      ],
      defaultValue: {
        mainMenuText: {
          "@type": "@builder.io/core:LocalizedValue",
          Default: "Main Menu",
        },
        backToText: {
          "@type": "@builder.io/core:LocalizedValue",
          Default: "Back to",
        },
        dlt: "Secondary navigation",
      },
    },
    {
      name: "dropdowns",
      type: "list",
      subFields: [
        { ...TITLE_FIELD, required: false },
        {
          ...LEGACY_HREF_FIELD,
          required: false,
          helperText:
            "Displays as a regular link when filled; otherwise, it functions as a dropdown.",
        },
        { ...DLD_FIELD, required: false },
        { ...DLT_FIELD, required: false },
        {
          name: "columns",
          type: "list",
          subFields: [
            { ...TITLE_FIELD, required: false },
            { ...DLD_FIELD, required: false },
            { ...DLT_FIELD, required: false },
            {
              name: "links",
              type: "list",
              subFields: [
                { ...TITLE_FIELD, required: false },
                { ...LEGACY_HREF_FIELD, required: false },
                { ...DLD_FIELD, required: false },
                { ...DLT_FIELD, required: false },
              ],
            },
          ],
        },
        {
          name: "rightLink",
          type: "object",
          subFields: [
            { ...TITLE_FIELD, required: false },
            { ...LEGACY_HREF_FIELD, required: false },
            { ...DLD_FIELD, required: false },
            { ...DLT_FIELD, required: false },
          ],
        },
      ],
      defaultValue: [
        {
          title: {
            "@type": "@builder.io/core:LocalizedValue",
            Default: "Lorem ipsum",
          },
          dlt: "Secondary navigation",
          columns: [
            {
              title: {
                "@type": "@builder.io/core:LocalizedValue",
                Default: "Lorem ipsum",
              },
              links: [
                {
                  title: {
                    "@type": "@builder.io/core:LocalizedValue",
                    Default: "Lorem ipsum dolor sit amet",
                  },
                },
              ],
            },
          ],
          rightLink: {
            dlt: "Secondary navigation",
          },
        },
      ],
    },
    {
      name: "alwaysVisibleBlocks",
      friendlyName: "Always Visible Blocks",
      helperText: "Blocks that are always visible, even in mobile.",
      type: "uiBlocks",
      showIf: (options) => options.get("newSecondaryNavigation") !== true,
      defaultValue: [
        {
          "@type": "@builder.io/sdk:Element",
          component: {
            name: "Core:Button",
            options: {
              variant: "secondary",
              size: "xs",
              text: {
                "@type": "@builder.io/core:LocalizedValue",
                Default: "Always visible",
              },
              dlt: "Secondary navigation",
              dld: "",
            },
          },
          meta: {
            localizedTextInputs: ["text"],
            "transformed.text": "localized",
          },
        },
      ],
    },
    {
      name: "visibleWhenStickyBlocks",
      friendlyName: "Visible When Sticky Blocks",
      helperText:
        "Blocks that are only visible when the nav is sticky. Also not visible in mobile secondary nav, but visible on product mobile secondary nav.",
      type: "uiBlocks",
      defaultValue: [
        {
          "@type": "@builder.io/sdk:Element",
          component: {
            name: "Core:Button",
            options: {
              variant: "primary",
              size: "xs",
              text: {
                "@type": "@builder.io/core:LocalizedValue",
                Default: "Visible when sticky",
              },
              dlt: "Secondary navigation",
              dld: "",
            },
          },
        },
      ],
    },
  ],
};
